import {
  UserRole,
  type CommunityPost,
  formatRelative,
  type Locale,
  type RelativeUnit,
} from '@musicville/shared';
import {
  ArrowLink,
  Avatar,
  Badge,
  Section,
  SectionHeader,
  Skeleton,
} from '@/components/ui/primitives';
import { useI18n } from '@/lib/i18n';
import { useCommunity } from '@/lib/queries';

export function Community(): React.JSX.Element {
  const { t } = useI18n();
  const { data, isPending } = useCommunity();

  return (
    <Section subtle>
      <SectionHeader
        overline={t('community.overline')}
        title={t('community.title')}
        action={<ArrowLink to="/community">{t('nav.community')}</ArrowLink>}
      />

      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {isPending
          ? Array.from({ length: 2 }, (_, i) => <Skeleton key={i} className="h-64 rounded-lg" />)
          : data?.slice(0, 2).map((post) => <PostCard key={post.id} post={post} />)}
      </div>
    </Section>
  );
}

function PostCard({ post }: { post: CommunityPost }): React.JSX.Element {
  const { locale } = useI18n();

  return (
    <article className="flex flex-col gap-3.5 rounded-lg border border-border-subtle bg-surface px-6 pb-5 pt-6">
      <header className="flex items-center gap-2.5">
        <Avatar name={post.authorName} size="md" />

        <div className="flex flex-1 flex-col">
          <div className="flex items-center gap-1.5">
            <span className="text-body-sm font-medium text-primary">{post.authorName}</span>
            {post.authorRole === UserRole.TEACHER && (
              <Badge tone="info" solid>
                {UserRole.TEACHER.charAt(0) + UserRole.TEACHER.slice(1).toLowerCase()}
              </Badge>
            )}
          </div>
          <time dateTime={post.publishedAt} className="text-caption text-tertiary">
            {relativeTime(post.publishedAt, locale)}
          </time>
        </div>

        <span className="flex h-8 items-center rounded-sm border border-border-default px-4 text-body-sm font-medium text-primary">
          {post.tag}
        </span>
      </header>

      <h3 className="text-h4 font-semibold text-primary">{post.title}</h3>
      <p className="text-body text-secondary">{post.excerpt}</p>
    </article>
  );
}

function relativeTime(iso: string, locale: Locale): string {
  const seconds = (Date.parse(iso) - Date.now()) / 1000;

  const units: [RelativeUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];

  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatRelative(Math.round(seconds / size), unit, locale);
  }
  return formatRelative(Math.round(seconds), 'second', locale);
}
